import React, { useEffect, useState } from 'react';
import {
  Card, Table, Tag, Button, Space, Row, Col, Statistic, Select,
  Modal, Input, message, Drawer, Typography, Tooltip, Badge,
} from 'antd';
import {
  PhoneOutlined, CopyOutlined, CheckCircleOutlined,
  ClockCircleOutlined, ExclamationCircleOutlined, ReloadOutlined,
} from '@ant-design/icons';
import {
  getFollowUpTasks, getFollowUpTaskStats, getFollowUpTaskDetail, updateFollowUpTask,
} from '../services/adminApi';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

// 优先级配置
const PRIORITY_MAP: Record<string, { color: string; label: string }> = {
  urgent: { color: 'red', label: '紧急' },
  high: { color: 'orange', label: '高' },
  normal: { color: 'blue', label: '普通' },
  low: { color: 'default', label: '低' },
};

// 状态配置
const STATUS_MAP: Record<string, { color: string; label: string }> = {
  pending: { color: 'warning', label: '待处理' },
  contacted: { color: 'processing', label: '已联系' },
  no_answer: { color: 'default', label: '未接通' },
  converted: { color: 'success', label: '已转化' },
  need_followup: { color: 'purple', label: '需跟进' },
  cancelled: { color: 'default', label: '已取消' },
  expired: { color: 'error', label: '已过期' },
};

// 触发类型
const TRIGGER_MAP: Record<string, string> = {
  churn_warning: '流失预警',
  contract_expiry: '合同到期',
  usage_anomaly: '用电异常',
  new_policy: '新政策',
  periodic: '定期回访',
};

const FollowUpTasks: React.FC = () => {
  const [tasks, setTasks] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState<{ status?: string; priority?: string }>({});
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentTask, setCurrentTask] = useState<any>(null);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [updateForm, setUpdateForm] = useState<{ status: string; result: string }>({
    status: 'contacted', result: '',
  });

  const fetchTasks = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const res = await getFollowUpTasks({ page, pageSize, ...filters });
      const data = res.data?.data;
      if (data) {
        setTasks(data.list || []);
        setPagination({ current: data.page, pageSize: data.pageSize, total: data.total });
      }
    } catch (e) {
      message.error('获取任务列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await getFollowUpTaskStats();
      if (res.data?.data) setStats(res.data.data);
    } catch (_) {}
  };

  useEffect(() => { fetchTasks(); fetchStats(); }, []);
  useEffect(() => { fetchTasks(); }, [filters]);

  const handleViewDetail = async (taskId: number) => {
    try {
      const res = await getFollowUpTaskDetail(taskId);
      if (res.data?.data) {
        setCurrentTask(res.data.data);
        setDrawerVisible(true);
      }
    } catch (_) {
      message.error('获取任务详情失败');
    }
  };

  const handleUpdateStatus = async () => {
    if (!currentTask) return;
    try {
      await updateFollowUpTask(currentTask.id, updateForm);
      message.success('状态更新成功');
      setStatusModalVisible(false);
      setDrawerVisible(false);
      fetchTasks(pagination.current, pagination.pageSize);
      fetchStats();
    } catch (_) {
      message.error('更新失败');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success('已复制到剪贴板');
    });
  };

  const parseTalkingPoints = (raw: string): string[] => {
    try { return JSON.parse(raw); } catch { return [raw]; }
  };

  const columns = [
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (v: string) => {
        const cfg = PRIORITY_MAP[v] || { color: 'default', label: v };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: '客户',
      key: 'customer',
      width: 180,
      render: (_: any, record: any) => {
        const snapshot = record.customerSnapshot ? JSON.parse(record.customerSnapshot) : {};
        const name = snapshot.companyName || record.customer?.nickname || record.customer?.phone || '-';
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{name}</div>
            {record.customer?.phone && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                <PhoneOutlined /> {record.customer.phone}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: '回访原因',
      key: 'trigger',
      width: 200,
      render: (_: any, record: any) => (
        <div>
          <Tag>{TRIGGER_MAP[record.triggerType] || record.triggerType}</Tag>
          <div style={{ fontSize: 12, marginTop: 4, color: '#666' }}>{record.summary}</div>
        </div>
      ),
    },
    {
      title: '业务员',
      key: 'assignee',
      width: 120,
      render: (_: any, record: any) => record.assignee?.nickname || record.assignee?.phone || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (v: string) => {
        const cfg = STATUS_MAP[v] || { color: 'default', label: v };
        return <Badge status={cfg.color as any} text={cfg.label} />;
      },
    },
    {
      title: '截止时间',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (v: string) => {
        const d = new Date(v);
        const isOverdue = d < new Date();
        const text = d.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
        return <Text type={isOverdue ? 'danger' : undefined}>{text}</Text>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 140,
      render: (_: any, record: any) => (
        <Space>
          <Button type="link" size="small" onClick={() => handleViewDetail(record.id)}>
            详情
          </Button>
          {record.status === 'pending' && (
            <Button type="link" size="small" onClick={() => {
              setCurrentTask(record);
              setUpdateForm({ status: 'contacted', result: '' });
              setStatusModalVisible(true);
            }}>
              标记
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="待处理"
              value={stats.pending || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="已逾期"
              value={stats.overdue || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="已联系"
              value={stats.contacted || 0}
              prefix={<PhoneOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="已转化"
              value={stats.converted || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 任务列表 */}
      <Card
        title="回访任务"
        extra={
          <Space>
            <Select
              placeholder="状态筛选"
              allowClear
              style={{ width: 120 }}
              onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
              options={Object.entries(STATUS_MAP).map(([k, v]) => ({ value: k, label: v.label }))}
            />
            <Select
              placeholder="优先级"
              allowClear
              style={{ width: 100 }}
              onChange={(v) => setFilters((f) => ({ ...f, priority: v }))}
              options={Object.entries(PRIORITY_MAP).map(([k, v]) => ({ value: k, label: v.label }))}
            />
            <Button icon={<ReloadOutlined />} onClick={() => { fetchTasks(); fetchStats(); }}>
              刷新
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => fetchTasks(page, pageSize),
          }}
          size="middle"
        />
      </Card>

      {/* 任务详情抽屉 */}
      <Drawer
        title="回访任务详情"
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={480}
        extra={
          currentTask?.status === 'pending' && (
            <Button type="primary" onClick={() => {
              setUpdateForm({ status: 'contacted', result: '' });
              setStatusModalVisible(true);
            }}>
              标记结果
            </Button>
          )
        }
      >
        {currentTask && (
          <div>
            {/* 客户信息 */}
            <Card size="small" title="客户信息" style={{ marginBottom: 16 }}>
              <p><Text strong>客户：</Text>{currentTask.customer?.nickname || '-'}</p>
              <p><Text strong>电话：</Text>
                {currentTask.customer?.phone || '-'}
                {currentTask.customer?.phone && (
                  <Button type="link" size="small" icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(currentTask.customer.phone)} />
                )}
              </p>
              {currentTask.customer?.userProfile?.companyName && (
                <p><Text strong>公司：</Text>{currentTask.customer.userProfile.companyName}</p>
              )}
              {currentTask.customer?.userProfile?.contactPerson && (
                <p><Text strong>联系人：</Text>{currentTask.customer.userProfile.contactPerson}</p>
              )}
            </Card>

            {/* 回访原因 */}
            <Card size="small" title="回访原因" style={{ marginBottom: 16 }}>
              <Tag>{TRIGGER_MAP[currentTask.triggerType] || currentTask.triggerType}</Tag>
              <Paragraph style={{ marginTop: 8 }}>{currentTask.summary}</Paragraph>
            </Card>

            {/* 话术建议 */}
            <Card size="small" title="话术建议" style={{ marginBottom: 16 }}>
              {parseTalkingPoints(currentTask.talkingPoints).map((point: string, idx: number) => (
                <div key={idx} style={{
                  background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6,
                  padding: '12px', marginBottom: 8, position: 'relative',
                }}>
                  <Paragraph style={{ marginBottom: 0 }}>{point}</Paragraph>
                  <Tooltip title="复制话术">
                    <Button
                      type="text" size="small" icon={<CopyOutlined />}
                      style={{ position: 'absolute', top: 8, right: 8 }}
                      onClick={() => copyToClipboard(point)}
                    />
                  </Tooltip>
                </div>
              ))}
            </Card>

            {/* 任务状态 */}
            <Card size="small" title="任务状态">
              <p><Text strong>状态：</Text>
                <Badge status={(STATUS_MAP[currentTask.status]?.color || 'default') as any}
                  text={STATUS_MAP[currentTask.status]?.label || currentTask.status} />
              </p>
              <p><Text strong>优先级：</Text>
                <Tag color={PRIORITY_MAP[currentTask.priority]?.color}>
                  {PRIORITY_MAP[currentTask.priority]?.label || currentTask.priority}
                </Tag>
              </p>
              <p><Text strong>截止：</Text>{new Date(currentTask.dueDate).toLocaleDateString('zh-CN')}</p>
              {currentTask.contactedAt && (
                <p><Text strong>联系时间：</Text>{new Date(currentTask.contactedAt).toLocaleString('zh-CN')}</p>
              )}
              {currentTask.result && (
                <p><Text strong>沟通结果：</Text>{currentTask.result}</p>
              )}
            </Card>
          </div>
        )}
      </Drawer>

      {/* 状态更新弹窗 */}
      <Modal
        title="标记回访结果"
        open={statusModalVisible}
        onOk={handleUpdateStatus}
        onCancel={() => setStatusModalVisible(false)}
        okText="确认"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>状态：</Text>
          <Select
            value={updateForm.status}
            onChange={(v) => setUpdateForm((f) => ({ ...f, status: v }))}
            style={{ width: '100%', marginTop: 8 }}
            options={[
              { value: 'contacted', label: '已联系' },
              { value: 'no_answer', label: '未接通' },
              { value: 'converted', label: '已转化' },
              { value: 'need_followup', label: '需跟进' },
              { value: 'cancelled', label: '取消任务' },
            ]}
          />
        </div>
        <div>
          <Text strong>沟通结果：</Text>
          <TextArea
            rows={3}
            placeholder="简要记录沟通内容..."
            value={updateForm.result}
            onChange={(e) => setUpdateForm((f) => ({ ...f, result: e.target.value }))}
            style={{ marginTop: 8 }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default FollowUpTasks;
